// Zustand des Knoten-Spielers (rein, ohne DOM): Ansicht, Stelle u (0..1), Abspielen, Geschwindigkeit, Takt.
// Die Stelle ist EINE Größe für alle drei Ansichten — ein Wechsel der Ansicht behält sie (Schritt-Modus rundet auf
// die Schrittgrenze, weil er nur Schrittbilder zeigt). `steps` sind die END-Stellen der Schritte (wie knot.steps).
import { stepAt, uOfStep, stepView } from './rope.js';

export const MODES = ['steps', 'anim', 'list'];
export const STEP_MS = 2500; // Dauer je Schritt bei voller Geschwindigkeit
export const HOLD_MS = 1000; // Halt am Ende, bevor die Schleife von vorn beginnt
const EPS = 1e-9;
const clamp01 = (u) => Math.min(1, Math.max(0, Number(u) || 0));

export function createPlayerState({ steps, mode = 'steps', u = 0 } = {}) {
  const st = { steps: Array.isArray(steps) && steps.length ? steps.slice() : [1] };
  const bounds = [0, ...st.steps];
  let m = MODES.includes(mode) ? mode : 'steps';
  let pos = clamp01(u);
  let playing = false, speed = 1, hold = 0, acc = 0;
  const round = (v) => uOfStep(st, stepAt(st, v));
  if (m === 'steps') pos = round(pos);
  // kleinste Grenze, unter der der Schritt-Modus nicht zurückgeht (Schritt 1)
  const floor = () => (m === 'steps' ? st.steps[0] : 0);
  const nextBound = (v) => bounds.find((b) => b > v + EPS);
  const prevBound = (v) => [...bounds].reverse().find((b) => b < v - EPS);

  const api = {
    get mode() { return m; },
    get u() { return pos; },
    get playing() { return playing; },
    get speed() { return speed; },
    get step() { return stepAt(st, pos); },
    get steps() { return st.steps; },
    get canPrev() { return pos > floor() + EPS; },
    get canNext() { return pos < 1 - EPS; },
    setMode(next) {
      if (!MODES.includes(next)) return api;
      m = next;
      if (m !== 'anim') playing = false;
      if (m === 'steps') pos = round(pos);
      hold = 0; acc = 0;
      return api;
    },
    setU(v) {
      pos = clamp01(v);
      if (m === 'steps') pos = round(pos);
      hold = 0; acc = 0;
      return api;
    },
    next() { const b = nextBound(pos); if (b != null) api.setU(b); return api; },
    prev() { const b = prevBound(pos); if (b != null && b >= floor() - EPS) api.setU(b); return api; },
    toggle() {
      playing = !playing;
      if (playing && pos >= 1 - EPS) pos = 0;
      hold = 0; acc = 0;
      return api;
    },
    /** Abspielen ohne Zurücksetzen (automatischer Start beim Öffnen der Animation). */
    play() { if (m === 'anim') { playing = true; hold = 0; acc = 0; } return api; },
    pause() { playing = false; return api; },
    setSpeed(s) { if (s === 1 || s === 0.5) speed = s; return api; },
    /**
     * Takt der Animation um dt Millisekunden weiterrücken. Gibt true zurück, wenn sich u geändert hat.
     * reduced: keine Zwischenbewegung — u springt nach Ablauf einer Schrittdauer auf die nächste Grenze.
     */
    tick(dt, { reduced = false } = {}) {
      if (!playing || m !== 'anim' || !(dt > 0)) return false;
      if (pos >= 1 - EPS) {
        hold += dt;
        if (hold < HOLD_MS) return false;
        hold = 0; acc = 0; pos = 0;
        return true;
      }
      const dur = STEP_MS / speed;
      if (reduced) {
        acc += dt;
        if (acc < dur) return false;
        acc = 0;
        pos = nextBound(pos) ?? 1;
        return true;
      }
      let left = dt;
      while (left > 0 && pos < 1 - EPS) {
        const b = nextBound(pos), a = prevBound(b) ?? 0;
        const rate = (b - a) / dur; // u je ms in diesem Schritt
        const need = (b - pos) / rate;
        if (need > left) { pos += rate * left; left = 0; } else { pos = b; left -= need; }
      }
      if (pos >= 1 - EPS) pos = 1;
      return true;
    },
  };
  return api;
}

export { stepView }; // liegt in rope.js (auch serverseitig gebraucht)
